import React, { useState } from 'react';
import './SignupPopup.css';
import { useAuth } from '../context/AuthContext';
import { toast } from 'react-toastify';
import { useFormValidation } from '../hooks/useFormValidation';
import { VALIDATION_RULES } from '../utils/validation';
import EmailVerificationOTP from './EmailVerificationOTP';
import apiService from '../services/apiService';


function SignupPopup({ onClose, onLoginClick }) {
  const { register, loading } = useAuth();
  const [showOtpVerification, setShowOtpVerification] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);

  // Initialize form validation
  const {
    formData,
    isValid,
    handleInputChange,
    handleFieldBlur,
    handleSubmit,
    getFieldValidationAttributes,
    hasError,
    getFieldError
  } = useFormValidation(
    {
      first_name: '',
      last_name: '',
      email: '',
      password: '',
      phone_number: ''
    },
    {
      first_name: VALIDATION_RULES.name,
      last_name: VALIDATION_RULES.name,
      email: VALIDATION_RULES.email,
      password: VALIDATION_RULES.password,
      phone_number: VALIDATION_RULES.phone
    },
    { validateOnBlur: true }
  );

  const onSubmit = async (data) => {
    setIsRegistering(true);
    try {
      const userData = {
        first_name: data.first_name,
        last_name: data.last_name,
        email: data.email,
        password: data.password,
        phone_number: data.phone_number || null,
      };
      await register(userData);
      
      // Send verification OTP
      try {
        await apiService.post('/auth/send-email-verification-otp', {
          email: data.email,
        });
        
        setRegisteredEmail(data.email);
        setShowOtpVerification(true);
        toast.success('Account created! Please verify your email.');
      } catch (otpError) {
        console.error('Failed to send OTP:', otpError);
        toast.error('Account created, but failed to send verification email. Please try again.');
      }
    } catch (err) {
      console.error("Signup failed:", err);
    } finally {
      setIsRegistering(false);
    }
  };

  const handleVerificationComplete = () => {
    setShowOtpVerification(false);
    setRegisteredEmail('');
    toast.success('Email verified! You can now log in.');
    onLoginClick();
  };

  const handleBackToSignup = () => {
    setShowOtpVerification(false);
    setRegisteredEmail('');
  };

  if (showOtpVerification) {
    return (
      <EmailVerificationOTP
        email={registeredEmail}
        onVerificationComplete={handleVerificationComplete}
        onBackToSignup={handleBackToSignup}
      />
    );
  }

  return (
    <div className="signup-popup-overlay" onClick={onClose}>
      <div className="signup-popup" onClick={(e) => e.stopPropagation()}>
        <button className="close-button" onClick={onClose}>&times;</button>
        <h2>SIGN UP</h2>
        <form onSubmit={handleSubmit(onSubmit)} className="signup-form">
          <div className="form-group">
            <label htmlFor="signup-first-name">First Name</label>
            <input
              type="text"
              id="signup-first-name"
              name="first_name"
              value={formData.first_name}
              onChange={handleInputChange}
              onBlur={handleFieldBlur}
              {...getFieldValidationAttributes('first_name')}
            />
            {hasError('first_name') && (
              <p className="error-message" id="first_name-error">
                {getFieldError('first_name')}
              </p>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="signup-last-name">Last Name</label>
            <input
              type="text"
              id="signup-last-name"
              name="last_name"
              value={formData.last_name}
              onChange={handleInputChange}
              onBlur={handleFieldBlur}
              {...getFieldValidationAttributes('last_name')}
            />
            {hasError('last_name') && (
              <p className="error-message" id="last_name-error">
                {getFieldError('last_name')}
              </p>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="signup-email">Email</label>
            <input
              type="email"
              id="signup-email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              onBlur={handleFieldBlur}
              {...getFieldValidationAttributes('email')}
            />
            {hasError('email') && (
              <p className="error-message" id="email-error">
                {getFieldError('email')}
              </p>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="signup-password">Password</label>
            <input
              type="password"
              id="signup-password"
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              onBlur={handleFieldBlur}
              {...getFieldValidationAttributes('password')}
            />
            {hasError('password') && (
              <p className="error-message" id="password-error">
                {getFieldError('password')}
              </p>
            )}
          </div>
          <div className="form-group">
            <label htmlFor="signup-phone-number">Phone Number (Optional)</label>
            <input
              type="tel"
              id="signup-phone-number"
              name="phone_number"
              value={formData.phone_number}
              onChange={handleInputChange}
              onBlur={handleFieldBlur}
              {...getFieldValidationAttributes('phone_number')}
            />
            {hasError('phone_number') && (
              <p className="error-message" id="phone_number-error">
                {getFieldError('phone_number')}
              </p>
            )}
          </div>
          <button type="submit" className="signup-button" disabled={loading || !isValid || isRegistering}>
            {loading || isRegistering ? 'Creating Account...' : 'Sign Up'}
          </button>
          <button type="button" onClick={onLoginClick} className="popup-switch-btn">
            Already have an account? Login
          </button>
        </form>
      </div>
    </div>
  );
}

export default SignupPopup;
